import { trackEvent } from '../../services/analytics';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import React, { useState, useEffect, useLayoutEffect } from 'react';
import dayjs from 'dayjs';
import SearchBar from '../utilities/SearchBar';
import { useNavigate, useParams } from 'react-router-dom';
import { useJsApiLoader } from '@react-google-maps/api';
import {
    Box,
    Typography,
    Button,
    IconButton,
    Avatar,
    CircularProgress,
} from '@mui/material';
import { Add, Remove, LocationOn, AccountCircle } from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import Header from '../layout/Header';
import Inputs from '../utilities/Inputs';
import ModalComponent from '../utilities/ModalComponent';
import InterestsSelection from '../utilities/InterestsOptions';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { Snackbar, Alert } from '@mui/material';
import { TableCreateView, TableDetailView } from './TableViews';
import { useAuth } from '../../context/AuthContext';
import { clearSessionCache, getSessionCache, loadSessionData, sessionCacheKey } from '../../utils/sessionCache';
import { hasTableParticipationProfile } from '../../utils/tableAccess';
import { WHATSAPP_COMMUNITY_URL } from '../../utils/community';
import { getPlaceCity } from '../../utils/tablePresentation';
import { requestNativeLogin, requestNativeShare } from '../../services/nativeAppBridge';
import {
    cancelGroup,
    createGroup,
    getGroupDetails,
    getUserProfile,
    getLeaveReasonOptions,
    getTableRuleOptions,
    joinGroup,
    leaveGroup,
    reportGroup,
    resolveGroupLink,
    updateGroup,
} from '../../services/firebaseData';

const GOOGLE_MAP_LIBRARIES = ['places', 'geometry'];
const DEFAULT_COVER_IMAGE = 'https://images.unsplash.com/photo-1414235077428-338989a2e8c0?auto=format&fit=crop&w=900&q=80';

function getCoverSource(imageUrl, metadata) {
    if (metadata?.coverSource === 'custom' || metadata?.coverSource === 'venue') {
        return metadata.coverSource;
    }
    // Older tables did not record the source. Uploaded covers use Firebase Storage;
    // Google Places photo URLs and the old stock image are venue defaults.
    if (!imageUrl || imageUrl === DEFAULT_COVER_IMAGE ||
        imageUrl.includes('googleapis.com/maps/api/place/') || imageUrl.includes('googleusercontent.com')) {
        return 'venue';
    }
    return 'custom';
}

export default function Table() {
    const { currentUser } = useAuth();
    const userId = currentUser && !currentUser.isAnonymous ? currentUser.uid : null;
    const lastPlace = localStorage.getItem('lastPlace');
    const lastLat = localStorage.getItem('lastLat');
    const lastLng = localStorage.getItem('lastLng');
    const { group_id } = useParams();
    const [detailLoad, setDetailLoad] = useState(() => ({
        groupId: group_id,
        status: group_id ? 'loading' : 'ready',
    }));
    const [detailRetry, setDetailRetry] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [participants, setParticipants] = useState([]);
    const [attendeeCount, setAttendeeCount] = useState(0);
    const [numberParticipants, setNumberParticipants] = useState(5);
    const [tableTheme, setTableTheme] = useState('');

    const { isLoaded: isGoogleMapsLoaded } = useJsApiLoader({
        googleMapsApiKey: process.env.REACT_APP_MAPS_API_KEY,
        libraries: GOOGLE_MAP_LIBRARIES,
        language: 'it',
    });

    const [tableStartAt, setTableStartAt] = useState(() => {
        const now = new Date();

        // today at 19:00
        const todayAt19 = new Date();
        todayAt19.setHours(19, 0, 0, 0);

        if (todayAt19 > now) {
            return todayAt19; // still in the future
        }

        // otherwise tomorrow at 19:00
        const tomorrowAt19 = new Date();
        tomorrowAt19.setDate(tomorrowAt19.getDate() + 1);
        tomorrowAt19.setHours(19, 0, 0, 0);
        return tomorrowAt19;
    });

    const [tableEndAt, setTableEndAt] = useState(null);
    const [tableLocationName, setTableLocationName] = useState(() => {
        const storedPlace = localStorage.getItem('searchedPlace');
        if (!storedPlace) return '';
        try {
            const parsed = JSON.parse(storedPlace);
            return (parsed.place || '') + ', ' + (parsed.directions || '');
        } catch (e) {
            console.error('Errore nel parsing di searchedPlace:', e);
            return '';
        }
    });
    const [description, setDescription] = useState('');
    const [interests, setInterests] = useState(['Convivial']);
    const [visibility, setVisibility] = useState('public');
    const [selectedRuleIds, setSelectedRuleIds] = useState(['discretion']);
    const [tableRuleOptions, setTableRuleOptions] = useState([]);
    const [coverImageUrl, setCoverImageUrl] = useState(DEFAULT_COVER_IMAGE);
    const [coverFile, setCoverFile] = useState(null);
    const [coverSource, setCoverSource] = useState('venue');
    const [errorMessage, setErrorMessage] = useState('');
    const [tableVenue, setTableVenue] = useState(() => group_id ? null : localStorage.getItem('tableVenue') || null);
    const [groupUrl, setGroupUrl] = useState('');
    const [infoMessage, setInfoMessage] = useState('');
    const [latLonPlace, setLatLonPlace] = useState({
        lat: lastLat,
        lng: lastLng,
        place: lastPlace,
        directions: lastPlace,
        city: '',
        phone: '',
    });
    const [modalErrorOpen, setModalErrorOpen] = useState(false);
    const [modalErrorMessage, setModalErrorMessage] = useState('');
    const [tableId, setTableId] = useState(null);
    const theme = useTheme();
    const navigate = useNavigate();
    const [status, setStatus] = useState('undefined');
    const [refreshView, setRefreshView] = useState(true);
    const [isUserAttending, setIsUserAttending] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [modalType, setModalType] = useState(null); // null, 'created', 'joined', 'updated', 'reported'
    const [requestState, setRequestState] = useState('none'); // none, pending, accepted, rejected, cancelled
    const [leaveReasonOptions, setLeaveReasonOptions] = useState([]);
    const [selectedLeaveReasons, setSelectedLeaveReasons] = useState([]);
    const [isCreator, setIsCreator] = useState(false);

    const [viewState, setViewState] = useState('create');
    const [creationProfileStatus, setCreationProfileStatus] = useState('loading');
    const [profileCheckRetry, setProfileCheckRetry] = useState(0);

    // Check the saved profile before displaying any step of table creation.
    // Direct links to /table use the same check as the bottom navigation.
    useEffect(() => {
        if (group_id || !userId) return;
        let active = true;
        setCreationProfileStatus('loading');
        getUserProfile(userId, userId).then((user) => {
            if (!active) return;
            const complete = hasTableParticipationProfile(user);
            setCreationProfileStatus(complete ? 'ready' : 'incomplete');
        }).catch(() => {
            if (active) setCreationProfileStatus('error');
        });
        return () => { active = false; };
    }, [group_id, userId, profileCheckRetry]);

    const ensureParticipationProfile = async (nextModalType) => {
        if (!userId) {
            const redirectTo = group_id ? `/table/${group_id}` : '/table';
            const handledByNative = requestNativeLogin(redirectTo);

            if (!handledByNative) {
                navigate('/login', { state: { redirectTo } });
            }
            return false;
        }

        try {
            const profile = await getUserProfile(userId, userId);
            if (hasTableParticipationProfile(profile)) return true;
            setModalType(nextModalType);
            return false;
        } catch (error) {
            console.error('Errore durante la verifica del numero di telefono:', error);
            setModalType(nextModalType);
            return false;
        }
    };

    const goToOwnProfile = () => {
        setModalType(null);
        navigate('/user');
    };

    const handleSearchSelection = async (selectedPlace) => {
        const googlePlaces = window.google?.maps?.places;
        if (!googlePlaces?.PlacesService || !googlePlaces?.PlacesServiceStatus) {
            setModalErrorOpen(true);
            setModalErrorMessage('La ricerca dei luoghi si sta ancora caricando. Riprova tra qualche secondo.');
            return;
        }
        const div = document.createElement('div');
        const service = new googlePlaces.PlacesService(div);
        service.getDetails(
            {
                placeId: selectedPlace.placeId,
                fields: [
                    "name",
                    "formatted_address",
                    "address_components",
                    "geometry",
                    "international_phone_number",
                    "photos",
                    "place_id",
                ],
            },
            (place, status) => {
                if (status === googlePlaces.PlacesServiceStatus.OK) {

                    const placePhoto = place.photos?.[0]
                        ? place.photos[0].getUrl({ maxWidth: 1200, maxHeight: 700 })
                        : selectedPlace.photo || '';

                    const searchedPlace = {
                        placeId: place.place_id || selectedPlace.placeId || '',
                        lat: place.geometry.location.lat(),
                        lng: place.geometry.location.lng(),
                        place: place.name || "",
                        directions: place.formatted_address || "",
                        city: getPlaceCity(place),
                        phone: place.international_phone_number || "",
                        photo: placePhoto,
                    };

                    setLatLonPlace(searchedPlace);
                    setTableVenue(searchedPlace.placeId || null);

                    if (coverSource === 'venue') {
                        setCoverImageUrl(placePhoto || DEFAULT_COVER_IMAGE);
                    }

                    setTableLocationName(place.name + ' (' + place.formatted_address + ')');
                    localStorage.setItem("lastQueryPlace", place.name || "");
                    localStorage.setItem("searchedPlace", JSON.stringify(searchedPlace));

                    console.log('searchplace B', searchedPlace);
                }
            }
        );
    };

    const handleCreateTable = async () => {
        trackEvent('action_start', { action: 'create_table' });
        setIsLoading(true);

        const hasCompleteProfile = await ensureParticipationProfile('phone_required_create');
        if (!hasCompleteProfile) {
            setIsLoading(false);
            return;
        }

        let errorMessages = [];

        if (tableTheme === '' || tableTheme === undefined) {
            errorMessages.push('Il nome del tavolo è obbligatorio.');
        }

        if (tableLocationName === '' || tableLocationName === undefined) {
            errorMessages.push('L\'indirizzo del tavolo è obbligatorio.');
        }

        if (tableStartAt === '' || tableStartAt === undefined) {
            errorMessages.push('La data di inizio è obbligatoria.');
        }

        if (numberParticipants === 0 || numberParticipants === undefined) {
            errorMessages.push('Il numero di partecipanti è obbligatorio.');
        }

        if (errorMessages.length > 0) {
            setIsLoading(false);

            setModalErrorOpen(true);
            setModalErrorMessage(errorMessages[0]);
            trackEvent('form_error', { form_name: 'table_create' });

            return;
        } else {
            setErrorMessage('');
        }

        try {
            const result = await createGroup(userId, {
                location: latLonPlace,
                name: tableTheme,
                venueId: tableVenue,
                description,
                limit: numberParticipants,
                interests,
                startAt: tableStartAt,
                imageUrl: coverSource === 'custom' ? coverImageUrl : '',
                coverFile,
                metadata: {
                    coverSource,
                    visibility,
                    ruleIds: selectedRuleIds,
                    phoneVisibility: 'HOST_AND_ATTENDEE',
                },
            });
            setTableId(result.id);
            setGroupUrl(result.url);
            trackEvent('table_created');
            setModalType('created');
        } catch (error) {
            console.error('Errore durante la richiesta:', error);
            setModalErrorMessage('Non è stato possibile creare il tavolo. ' + (error.code === 'permission-denied'
                ? 'Verifica il profilo e riprova.'
                : error.message || 'Riprova tra poco.'));
            setModalErrorOpen(true);
        } finally {
            setIsLoading(false);
        }
    };

    const handleCancelAttendee = async (leaveReasonIds = selectedLeaveReasons) => {
        setIsLoading(true);
        try {
            await leaveGroup(group_id, userId, leaveReasonIds);
            trackEvent('table_left');
            setIsUserAttending(false);
            setParticipants(prev => prev.filter(p => p.userId !== userId));
            setAttendeeCount(prev => Math.max(Number(prev || 0) - 1, 0));
            setSnackbarMessage('Iscrizione cancellata con successo');
            setSelectedLeaveReasons([]);
            setViewState('view');
        } catch (error) {
            console.error('Errore durante la richiesta:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleCancelTable = async () => {
        setIsLoading(true);

        if (Number(attendeeCount || participants.length || 0) > 1) {
            setIsLoading(false);
            setErrorMessage('Non puoi cancellare un tavolo con dei partecipanti.');
            return;
        } else {
            setErrorMessage('');
        }

        try {
            await cancelGroup(group_id, userId);
            trackEvent('table_cancelled');
            navigate('/');
        } catch (error) {
            console.error('Errore durante la richiesta:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleUpdateTable = async () => {
        setIsLoading(true);

        if (
            tableTheme === '' ||
            tableLocationName === '' ||
            tableLocationName === undefined ||
            tableStartAt === '' ||
            numberParticipants === 0
        ) {
            setIsLoading(false);
            setErrorMessage('Alcuni campi obbligatori non sono compilati');
            return;
        } else {
            setErrorMessage('');
        }

        try {
            const result = await updateGroup(group_id, userId, {
                location: latLonPlace,
                venueId: tableVenue,
                name: tableTheme,
                description,
                limit: numberParticipants,
                startAt: tableStartAt,
                endAt: tableEndAt,
                interests,
                imageUrl: coverSource === 'custom' ? coverImageUrl : '',
                coverFile,
                metadata: {
                    coverSource,
                    visibility,
                    ruleIds: selectedRuleIds,
                    phoneVisibility: 'HOST_AND_ATTENDEE',
                },
            });
            setTableId(result.id);
            setModalType('updated');
            trackEvent('table_updated');
        } catch (error) {
            console.error('Errore durante la richiesta:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleJoinTable = async () => {
        trackEvent('action_start', { action: 'join_table' });
        if (!userId) {
            const redirectTo = group_id ? `/table/${group_id}` : '/table';
            const handledByNative = requestNativeLogin(redirectTo);

            if (!handledByNative) {
                navigate('/login', { state: { redirectTo } });
            }
            return;
        }

        if (participants.some(p => (p.userId || p.user_id) === userId)) {
            setErrorMessage('Sei già iscritto a questo tavolo');
            return;
        }

        setIsLoading(true);
        const hasCompleteProfile = await ensureParticipationProfile('phone_required_join');
        if (!hasCompleteProfile) {
            setIsLoading(false);
            setRequestState('cancelled');
            return;
        }

        setRequestState('pending');

        if (viewState !== 'view') {
            setIsLoading(false);
            setRequestState('cancelled');
            setErrorMessage('Non puoi partecipare a questo tavolo');
            return;
        } else {
            setErrorMessage('');
        }

        try {
            await joinGroup(group_id, userId);
            trackEvent('table_joined');
            setRequestState('accepted');
            setIsUserAttending(true);
            setParticipants(prev => [...prev, {
                userId,
                displayName: localStorage.getItem('user_complete_name'),
                photoURL: localStorage.getItem('user_avatar_url'),
                role: 'ATTENDEE',
                user_id: userId,
                user_complete_name: localStorage.getItem('user_complete_name'),
                user_avatar_url: localStorage.getItem('user_avatar_url'),
                user_role: 'ATTENDEE',
            }]);
            setAttendeeCount(prev => Number(prev || 0) + 1);
            setViewState('attendee');
            setRefreshView(!refreshView);
            setModalType('joined');
        } catch (error) {
            setRequestState('rejected');
            console.error('Errore durante la richiesta:', error);
            setErrorMessage(error.message || 'Non è stato possibile iscriversi. Riprova.');
        } finally {
            setIsLoading(false);
        }
    };

    const handleJoinRequest = async () => {
        if (!userId) {
            const redirectTo = group_id ? `/table/${group_id}` : '/table';
            const handledByNative = requestNativeLogin(redirectTo);

            if (!handledByNative) {
                navigate('/login', { state: { redirectTo } });
            }
            return;
        }
        setIsLoading(true);
        const hasCompleteProfile = await ensureParticipationProfile('phone_required_join');
        setIsLoading(false);
        if (!hasCompleteProfile) return;
        setModalType('join_warning');
    };

    const handleOpenChat = () => {
        trackEvent('action_start', { action: 'open_chat' });
        navigate(`/table/${group_id}/chat`);
    };

    const handleOpenWaitlist = () => {
        window.location.href = WHATSAPP_COMMUNITY_URL;
    };

    const handleShareTable = async () => {
        trackEvent('action_start', { action: 'share_table' });
        const title = 'Condividi il Tavolo';
        const text = `Ti invito a partecipare al tavolo ${tableTheme}!`;
        const url = groupUrl;

        const handledByNative = requestNativeShare({
            title,
            text,
            url,
        });

        if (handledByNative) {
            return;
        }

        // Prima scelta: API Web Share (moderna, se disponibile)
        if (navigator.share) {
            try {
                await navigator.share({
                    title,
                    text,
                    url,
                });
                return;
            } catch (error) {
                console.error('Errore nella condivisione con Web Share API:', error);
            }
        }

        // Seconda scelta: WebViewGold bridge per share nativo
        try {
            // const nativeShareURL = `webviewgold://share?text=${encodeURIComponent(`${text} ${url}`)}`;
            const nativeShareURL = `shareapp://message?=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`;
            window.location.href = nativeShareURL;
            return;
        } catch (error) {
            console.error('Errore nella condivisione con WebViewGold:', error);
        }

        // Terza scelta: Fallback manuale
        try {
            await navigator.clipboard.writeText(`${text} ${url}`);
            alert('Link copiato negli appunti! Incollalo per condividerlo.');
        } catch (error) {
            alert(`Non è stato possibile copiare il link. Copialo manualmente:\n\n${text} ${url}`);
        }
    };


    const handleReportTable = async () => {
        await reportGroup(group_id, userId);
        setModalType('reported');
    };

    // const handlePlaceChanged = () => {
    //     if (autocomplete) {
    //         const place = autocomplete.getPlace();
    //         setLatLonPlace({
    //             lat: place.geometry.location.lat(),
    //             lng: place.geometry.location.lng(),
    //             place: place.name,
    //             directions: place.formatted_address,
    //             phone: place.international_phone_number || ''
    //         });
    //         setTableLocationName(place.name + ' (' + place.formatted_address + ')');
    //     }
    // };
    const handleAddParticipant = () => {
        setNumberParticipants((prev) => (prev < 8 ? prev + 1 : prev));
    };

    const handleRemoveParticipant = () => {
        setNumberParticipants((prev) => (prev > 2 ? prev - 1 : prev));
    };

    const handleCloseModal = () => {
        setModalType(null);
        if (modalType === 'created' || modalType === 'updated') {
            navigate(`/table/${tableId}`);
        }
    };

    // centerFromLocalStorage
    const centerFromLocalStorage = () => {
        const lat = parseFloat(localStorage.getItem('lastLat'));
        const lng = parseFloat(localStorage.getItem('lastLng'));
        return { lat: lat, lng: lng };
    };

    useLayoutEffect(() => {
        if (!group_id) return;
        let active = true;
        const cacheKey = sessionCacheKey('table', group_id, userId);
        const cached = getSessionCache(cacheKey);
        setDetailLoad({ groupId: group_id, status: 'loading' });
        setErrorMessage('');

        const getCurrentTableInfo = async () => {
            try {
                const data = cached || await loadSessionData(cacheKey, () => getGroupDetails(group_id, userId));
                if (!active) return;
                const groupData = data.group;
                if (!groupData) {
                    if (/^[a-z0-9]{20}$/.test(group_id)) {
                        const canonicalGroupId = await resolveGroupLink(group_id);
                        if (!active) return;
                        if (canonicalGroupId && canonicalGroupId !== group_id) {
                            navigate(`/table/${canonicalGroupId}`, { replace: true });
                            return;
                        }
                    }
                    setDetailLoad({ groupId: group_id, status: 'not-found' });
                    return;
                }

                // if (!response.ok) throw new Error('Network response was not ok');

                // Imposta i dati del tavolo
                const startAt = dayjs(groupData.startAt);
                const endAt = dayjs(groupData.endAt);
                const jsonInfoLocation = groupData.location || {};
                // console.log(jsonInfoLocation);
                setInterests(groupData.interests);

                setTableTheme(groupData.name);
                setGroupUrl(groupData.url);
                setTableVenue(groupData.venueId || jsonInfoLocation.placeId || null);
                setNumberParticipants(groupData.limit);
                setDescription(groupData.description);
                setVisibility(groupData.metadata?.visibility || 'public');
                setSelectedRuleIds(Array.isArray(groupData.metadata?.ruleIds) ? groupData.metadata.ruleIds : ['discretion']);
                const savedCoverSource = getCoverSource(groupData.imageUrl, groupData.metadata);
                setCoverSource(savedCoverSource);
                setCoverImageUrl(savedCoverSource === 'custom' ? groupData.imageUrl : DEFAULT_COVER_IMAGE);
                setCoverFile(null);
                setParticipants(data.attendees);
                setAttendeeCount(Number(groupData.attendeeCount || data.attendees.length || 0));
                setTableStartAt(startAt);
                setTableEndAt(endAt);

                // this is not necessary as it's exactly as jsonInfoLocation itself, so. it's redundant
                // const selectedPlace = {
                //     lat: jsonInfoLocation.lat,
                //     lng: jsonInfoLocation.lng,
                //     place: jsonInfoLocation.place || "",
                //     directions: jsonInfoLocation.directions || "",
                //     phone: jsonInfoLocation.phone || "",
                // };
                setTableLocationName(jsonInfoLocation.place + ' (' + jsonInfoLocation.directions + ')',);
                setLatLonPlace(jsonInfoLocation);

                console.log('searchplace C', jsonInfoLocation);


                // Controlla lo stato basato sui valori appena impostati
                let newStatus = null;
                const currentTime = dayjs();
                if (endAt && startAt) {
                    const liveStartsAt = startAt.subtract(1, 'hour');
                    if (endAt.isBefore(currentTime)) {
                        newStatus = 'terminato';
                    } else if (
                        liveStartsAt.isBefore(currentTime) &&
                        endAt.isAfter(currentTime)
                    ) {
                        newStatus = 'in corso';
                    } else {
                        newStatus = 'schedulato';
                    }
                }
                setStatus(newStatus);

                // Imposta lo stato di viewState in base ai partecipanti
                const currentMembership = groupData.currentUserMembership;
                const userIsCreator = currentMembership?.role === 'CREATOR' || groupData.createdBy === userId;
                const userIsAttendee = currentMembership?.status === 'JOINED' || data.attendees.some((participant) => participant.userId === userId);
                setIsCreator(Boolean(userIsCreator));

                let newViewState = 'view';
                if (userIsCreator && newStatus === 'schedulato') {
                    newViewState = 'edit';
                } else if (userIsAttendee) {
                    newViewState = 'attendee';
                }
                setViewState(newViewState);

                // Imposta un messaggio di stato basato su newViewState
                setInfoMessage(newStatus);

                if (newViewState === 'edit') setTableLocationName(jsonInfoLocation.place);
                setDetailLoad({ groupId: group_id, userId, status: 'ready' });
            } catch (error) {
                if (!active) return;
                console.error('Error fetching group data:', error);
                setDetailLoad({ groupId: group_id, status: 'error' });
            }
        };

        getCurrentTableInfo();
        return () => { active = false; };
    }, [group_id, userId, refreshView, detailRetry, navigate]);

    useEffect(() => {
        const userIsAttending = participants.some(p => p.userId === userId);
        setIsUserAttending(userIsAttending || isCreator);
    }, [participants, userId, isCreator]);

    useEffect(() => {
        let active = true;
        getTableRuleOptions().then((rules) => {
            if (active) setTableRuleOptions(rules);
        });
        getLeaveReasonOptions().then((reasons) => {
            if (active) setLeaveReasonOptions(reasons);
        });
        return () => {
            active = false;
        };
    }, []);

    useEffect(() => {
        const checkUserLogin = localStorage.getItem('user_id');
        if (!checkUserLogin && !group_id) {
            const handledByNative = requestNativeLogin('/table');

            if (!handledByNative) {
                navigate('/login', { state: { redirectTo: '/table' } });
            }
        }
    }, [navigate, group_id]);

    useEffect(() => {
        const lastQueryPlace = localStorage.getItem('lastQueryPlace');
        const searchedPlaceRaw = localStorage.getItem('searchedPlace');

        if (group_id || !searchedPlaceRaw) return;

        try {
            const parsed = JSON.parse(searchedPlaceRaw);

            setLatLonPlace({
                placeId: parsed.placeId || '',
                restaurantId: parsed.restaurantId || null,
                lat: parsed.lat,
                lng: parsed.lng,
                place: parsed.place,
                directions: parsed.directions,
                city: parsed.city || '',
                phone: parsed.phone || '',
            });
            setTableVenue(parsed.placeId || localStorage.getItem('tableVenue') || null);
            if (parsed.photo) setCoverImageUrl(parsed.photo);

            console.log('searchplace A', parsed);

            // solo se non hai lastQueryPlace setti anche tableLocationName
            if (!lastQueryPlace) {
                setTableLocationName(
                    (parsed.place || '') + (parsed.directions ? ` (${parsed.directions})` : '')
                );
            }
        } catch (e) {
            console.error('Errore nel parsing di searchedPlace:', e);
        }
    }, [group_id]);

    useEffect(() => {
        if (!isGoogleMapsLoaded || coverSource === 'custom' ||
            (!tableVenue && !(group_id && latLonPlace?.place))) return;
        const places = window.google?.maps?.places;
        if (!places?.PlacesService) return;

        let cancelled = false;
        const service = new places.PlacesService(document.createElement('div'));
        const showFirstPhoto = (place) => {
            if (cancelled) return;
            const photo = place?.photos?.[0];
            setCoverImageUrl(photo?.getUrl({ maxWidth: 1200, maxHeight: 700 }) || DEFAULT_COVER_IMAGE);
        };
        const loadPlace = (placeId) => {
            service.getDetails({ placeId, fields: ['photos'] }, (place, result) => {
                if (result === places.PlacesServiceStatus.OK) showFirstPhoto(place);
            });
        };

        if (tableVenue) {
            loadPlace(tableVenue);
        } else if (group_id && latLonPlace?.place) {
            // Older tables may have a venue name but no saved Google Place ID.
            const query = [latLonPlace.place, latLonPlace.directions].filter(Boolean).join(', ');
            service.findPlaceFromQuery({ query, fields: ['place_id'] }, (matches, result) => {
                if (!cancelled && result === places.PlacesServiceStatus.OK && matches?.[0]?.place_id) {
                    loadPlace(matches[0].place_id);
                }
            });
        }

        return () => { cancelled = true; };
    }, [isGoogleMapsLoaded, coverSource, tableVenue, group_id, latLonPlace?.place, latLonPlace?.directions]);


    // Legacy render kept temporarily during the UI migration; the active render is below.
    // eslint-disable-next-line no-unused-vars
    const legacyRender = () => (
        <>
            <Box sx={{ p: 2, mx: 'auto', mt: 4 }}>
                {/* Header */}
                {viewState === 'create' ? (
                    <Header
                        title="Proponi un nuovo tavolo"
                        topRightText={viewState === 'edit' ? 'Salva' : false}
                        topRightAction={
                            viewState === 'edit' ? () => handleCreateTable() : false
                        }
                    />
                ) : (
                    <Header title="Dettaglio Tavolo" statusIcon={status} />
                )}

                {/* Table Theme */}
                {viewState === 'create' || viewState === 'edit' ? (
                    <Inputs
                        type="text"
                        label="Nome / Tema del tavolo"
                        value={tableTheme}
                        onChange={(e) => setTableTheme(e.target.value)}
                        required
                        mt={5}
                        mb={3}
                    />
                ) : (
                    // view + attendee
                    <>
                        <Typography
                            variant="subtitle1"
                            color="textSecondary"
                            sx={{ mt: 3 }}
                        >
                            Nome / Tema del tavolo:
                        </Typography>
                        <Typography
                            variant="body1"
                            sx={{ mb: 2, fontWeight: 'bold', color: '#333' }}
                        >
                            {tableTheme || 'Nessun tema specificato'}
                        </Typography>
                    </>
                )}

                {latLonPlace.restaurantId && <Button startIcon={<RestaurantIcon />} onClick={() => navigate(`/restaurants/${latLonPlace.restaurantId}`)}>
                    Ristorante aderente · scopri il locale
                </Button>}
                {/* Table Location */}
                {(viewState === 'create' || viewState === 'edit') && !latLonPlace.restaurantId ? (
                    <SearchBar
                        onSearchSelection={handleSearchSelection}
                        textInside={tableLocationName || ''}
                        center={centerFromLocalStorage()}
                    />
                ) : (
                    // view + attendee
                    <>
                        <Typography variant="subtitle1" color="textSecondary">
                            Indirizzo del luogo:
                        </Typography>
                        <Box
                            display="flex"
                            alignItems="center"
                            justifyContent="space-between"
                            sx={{ mb: 2 }}
                        >
                            <Typography
                                variant="body1"
                                sx={{ fontWeight: 'bold', color: '#333' }}
                            >
                                {tableLocationName || 'Nessun indirizzo specificato'}
                            </Typography>
                            {tableLocationName && (
                                <IconButton
                                    size="small"
                                    color="primary"
                                    onClick={() => {
                                        window.location.href = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(latLonPlace.directions)}`;
                                    }}
                                >
                                    <LocationOn />
                                </IconButton>
                            )}
                        </Box>
                    </>
                )}

                {/* Picker DataTime */}
                {viewState === 'create' || viewState === 'edit' ? (
                    <Inputs
                        type="date"
                        label="Scegli quando"
                        value={tableStartAt}
                        onChange={(newDate) => setTableStartAt(newDate)}
                        required
                        disablePast={viewState === 'create'}
                        mt={3}
                        mb={3}
                    />
                ) : (
                    // view + attendee
                    <>
                        <Typography variant="subtitle1" color="textSecondary">
                            Quando:
                        </Typography>
                        <Typography
                            variant="body1"
                            sx={{ mb: 2, fontWeight: 'bold', color: '#333' }}
                        >
                            {tableStartAt
                                ? dayjs(tableStartAt).format('dddd DD/MM/YYYY, HH:mm')
                                : 'Data e ora non specificate'}
                        </Typography>
                    </>
                )}

                {/* Interests of the table */}
                {viewState === 'create' || viewState === 'edit' ? (
                    <>
                        <Box alignItems="center" mt={3} mb={2}>
                            <InterestsSelection
                                viewState={viewState === 'create' ? 'edit' : viewState}
                                userInterests={[interests] || ['Convivial']}
                                setUserInterests={setInterests}
                                selectOption
                            />
                        </Box>
                    </>
                ) : (
                    <>
                        <Typography variant="subtitle1" color="textSecondary">
                            Interesse a parlare di:
                        </Typography>
                        <Typography
                            variant="body1"
                            sx={{ mb: 2, fontWeight: 'bold', color: '#333' }}
                        >
                            {interests}
                        </Typography>
                    </>
                )}

                {/* Maximum Number of Participants */}
                {(viewState === 'create' || viewState === 'edit') && (
                    <>
                        <Box display="flex" alignItems="center" mt={3} mb={2}>
                            <Typography variant="body1" sx={{ flexGrow: 1, mr: 2 }}>
                                Numero massimo di partecipanti *
                            </Typography>
                            <IconButton
                                onClick={handleRemoveParticipant}
                                sx={{
                                    backgroundColor: theme.palette.secondary.main,
                                    '&:hover': { backgroundColor: theme.palette.secondary.main },
                                    px: 1,
                                }}
                            >
                                <Remove sx={{ color: theme.palette.white.main }} />
                            </IconButton>
                            <Typography variant="h6" sx={{ mx: 1 }}>
                                {numberParticipants}
                            </Typography>
                            <IconButton
                                onClick={handleAddParticipant}
                                sx={{
                                    backgroundColor: theme.palette.secondary.main,
                                    '&:hover': { backgroundColor: theme.palette.secondary.main },
                                    px: 1,
                                }}
                            >
                                <Add sx={{ color: theme.palette.white.main }} />
                            </IconButton>
                        </Box>
                    </>
                )}

                {/* Participants */}
                {viewState !== 'create' && (
                    <>
                        <Box sx={{ mt: 3, mb: 2 }}>
                            <Typography variant="subtitle1" color="textSecondary">
                                Partecipanti: {participants.length}/{numberParticipants}
                            </Typography>
                            {participants.map((attendee) => (
                                <Box
                                    key={attendee.user_id}
                                    display="flex"
                                    alignItems="center"
                                    sx={{
                                        my: 1,
                                        bgcolor: attendee.user_id === userId ? theme.palette.action.selected : 'inherit',
                                        p: 1,
                                        borderRadius: 1
                                    }}
                                >
                                    <Avatar
                                        src={attendee.user_avatar_url}
                                        alt={attendee.user_complete_name}
                                        sx={{ mr: 2 }}
                                    />
                                    <Box sx={{ flexGrow: 1 }}>
                                        <Typography variant="body1">
                                            {attendee.user_complete_name}
                                        </Typography>
                                        <Typography variant="body2" color="textSecondary">
                                            {attendee.user_role === 'CREATOR'
                                                ? 'Organizzatore'
                                                : 'Partecipante'}
                                        </Typography>
                                    </Box>
                                    <IconButton
                                        size="small"
                                        color="primary"
                                        onClick={() => navigate(`/user/${attendee.user_id}`)}
                                    >
                                        <AccountCircle />
                                    </IconButton>
                                </Box>
                            ))}
                        </Box>
                    </>
                )}

                {/* Additional Notes */}
                {viewState === 'create' || viewState === 'edit' ? (
                    <Inputs
                        type="text"
                        label="Descrizione (opzionale)"
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        rows={5}
                    />
                ) : (
                    // view + attendee
                    <>
                        <Typography variant="subtitle1" color="textSecondary">
                            Descrizione:
                        </Typography>
                        <Typography variant="body1" sx={{ mb: 2, color: '#333' }}>
                            {description || 'Nessuna descrizione fornita'}
                        </Typography>
                    </>
                )}

                {viewState === 'edit' && latLonPlace.phone !== '' && (
                    <>
                        <Typography variant="subtitle1" color="textSecondary" sx={{ mt: 2 }}>
                            Telefono Ristorante:
                        </Typography>
                        <Typography variant="body1" sx={{ mb: 2, color: '#333' }}>
                            {latLonPlace.phone}
                        </Typography>
                    </>
                )}

                {/* Error Message */}
                {errorMessage && (
                    <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                        {errorMessage}
                    </Typography>
                )}
                {infoMessage && (
                    <Typography variant="body2" color="info" sx={{ mt: 1 }}>
                        Il tavolo è stato "{infoMessage}"
                    </Typography>
                )}

                {/* Submit Button */}
                {viewState === 'create' ? (
                    <Button
                        variant="contained"
                        color="primary"
                        fullWidth
                        onClick={handleCreateTable}
                        startIcon={isLoading && <CheckCircleIcon />}
                        disabled={isLoading}
                        sx={{
                            my: 1,
                            py: 1,
                            backgroundColor: theme.palette.primary.main,
                        }}
                    >
                        Crea Tavolo
                    </Button>
                ) : viewState === 'edit' ? (
                    <Button
                        variant="contained"
                        color="primary"
                        fullWidth
                        onClick={handleUpdateTable}
                        startIcon={isLoading && <CheckCircleIcon />}
                        disabled={isLoading}
                        sx={{
                            my: 1,
                            py: 1,
                            backgroundColor: theme.palette.primary.main,
                        }}
                    >
                        Aggiorna Tavolo
                    </Button>
                ) : (
                    viewState === 'view' &&
                    participants.length < numberParticipants &&
                    !participants.some(participant => participant.userId === userId) &&
                    status !== 'terminato' && (
                        <Button
                            variant="contained"
                            color={isUserAttending ? 'success' : 'primary'}
                            fullWidth
                            onClick={handleJoinTable}
                            disabled={isLoading || isUserAttending || participants.length >= numberParticipants}
                            startIcon={(isLoading || isUserAttending) && <CheckCircleIcon />}
                            sx={{
                                my: 1,
                                py: 1,
                                backgroundColor: theme.palette.primary.main,
                            }}
                        >
                            {isUserAttending ? 'Sei Iscritto' : 'Partecipa al Tavolo'}
                        </Button>
                    )
                )}

                {/* a tavolo terminato: puoi lasciare feedback */}
                {status === 'terminato' && participants.length > 1 && (
                    <Button
                        variant="contained"
                        color="secondary"
                        fullWidth
                        onClick={() => navigate('/feedback/' + group_id)}
                        startIcon={isLoading && <CheckCircleIcon />}
                        disabled={isLoading}
                        sx={{
                            my: 1,
                            py: 1,
                            backgroundColor: theme.palette.primary.main,
                            // animation: 'pulse 1s ease-out infinite',
                            '@keyframes pulse': {
                                '0%': { transform: 'scale(1)', opacity: 1 },
                                '50%': { transform: 'scale(1.01)', opacity: 1 },
                                '100%': { transform: 'scale(1)', opacity: 1 },
                            },
                        }}
                    >
                        Lascia un Feedback
                    </Button>
                )}

                {/* qui aggiungi il bottone per la condivisione solo se non è view */}
                { (
                    <Button
                        variant="contained"
                        color="secondary"
                        fullWidth
                        onClick={handleShareTable}
                        startIcon={isLoading && <CheckCircleIcon />}
                        disabled={isLoading}
                        sx={{
                            my: 1,
                            py: 1,
                            backgroundColor: theme.palette.secondary.main,
                        }}
                    >
                        Condividi Tavolo
                    </Button>
                )}

                {viewState !== 'create' && viewState !== 'edit' && (
                    <Button
                        variant="contained"
                        color="warning"
                        startIcon={isLoading && <CheckCircleIcon />}
                        disabled={isLoading}
                        fullWidth
                        onClick={handleReportTable}
                        sx={{
                            my: 1,
                            py: 1,
                        }}
                    >
                        Segnala il Tavolo
                    </Button>
                )}

                {/* Cancel Button */}
                {viewState === 'edit' && participants.length <= 1 && (
                    <Button
                        variant="contained"
                        color="danger"
                        fullWidth
                        onClick={handleCancelTable}
                        startIcon={isLoading && <CheckCircleIcon />}
                        disabled={isLoading}
                        sx={{
                            my: 1,
                            py: 1,
                            backgroundColor: theme.palette.danger.main,
                        }}
                    >
                        Cancella Tavolo
                    </Button>
                )}

                {/* Cancel Button */}
                {viewState === 'attendee' && dayjs() < tableStartAt && (
                    <Button
                        variant="contained"
                        color="danger"
                        fullWidth
                        onClick={handleCancelAttendee}
                        startIcon={isLoading && <CheckCircleIcon />}
                        disabled={isLoading}
                        sx={{
                            my: 1,
                            py: 1,
                            backgroundColor: theme.palette.danger.main,
                        }}
                    >
                        Cancella Partecipazione
                    </Button>
                )}

                <ModalComponent
                    modalOpen={modalType === 'created'}
                    onClose={handleCloseModal}
                    title="Tavolo creato con successo."
                    message={
                        (() => {
                            let returnedMessage = "Il tavolo è stato creato. \n\n";
                            returnedMessage += "ATTENZIONE: Ricordati di prenotare il locale. \n\n";
                            if (latLonPlace.phone !== '') {
                                returnedMessage += "Per particolari esigenze puoi contattare il locale al numero " + latLonPlace.phone + ".\n\n";
                            }
                            returnedMessage += "Visualizza al tavolo con il tasto qui sotto.";
                            return returnedMessage;
                        })()
                    }
                    buttons={[
                        {
                            text: 'Vai al tavolo',
                            onClick: handleCloseModal
                        },
                        ...(
                            latLonPlace.phone !== ''
                                ? [{
                                    text: 'Contatta il locale',
                                    onClick: () => window.open(`tel:${latLonPlace.phone}`),
                                    color: 'secondary'
                                }]
                                : []
                        )
                    ]}
                    disableBackdropClick
                />
                <ModalComponent
                    modalOpen={modalType === 'joined'}
                    onClose={() => setModalType(null)}
                    title="Ti sei iscritto al tavolo."
                    message="Sei ora iscritto al tavolo."
                    buttons={[
                        {
                            text: "Vai al tavolo",
                            onClick: () => setModalType(null)
                        }
                    ]}
                    disableBackdropClick={false}
                />
                <ModalComponent
                    modalOpen={modalType === 'updated'}
                    onClose={handleCloseModal}
                    title="Tavolo aggiornato con successo."
                    message="Il tuo tavolo è stato aggiornato. Puoi visualizzarlo con il tasto 'Vai al tavolo'."
                    buttons={[
                        {
                            text: "Vai al tavolo",
                            onClick: handleCloseModal
                        }
                    ]}
                    disableBackdropClick
                />
                <ModalComponent
                    modalOpen={modalType === 'reported'}
                    onClose={handleCloseModal}
                    title="Segnalazione inviata con successo."
                    message="Hai segnalato questo tavolo. Rimuoveremo il tavolo se riteniamo che violi il nostro codice di condotta."
                    buttons={[
                        {
                            text: "OK",
                            onClick: handleCloseModal
                        }
                    ]}
                    disableBackdropClick
                />


                <ModalComponent
                    modalOpen={modalErrorOpen}
                    onClose={() => setModalErrorOpen(false)}
                    title="Attenzione campi mancanti"
                    message={modalErrorMessage}
                    buttons={[
                        {
                            text: "Completa i dati",
                            onClick: () => setModalErrorOpen(false)
                        }
                    ]}
                    disableBackdropClick
                />

                <Snackbar
                    open={snackbarMessage !== ''}
                    autoHideDuration={6000}
                    onClose={() => setSnackbarMessage('')}
                >
                    <Alert
                        onClose={() => setSnackbarMessage('')}
                        severity="info"
                        sx={{ width: '100%' }}
                    >
                        {snackbarMessage}
                    </Alert>
                </Snackbar>
            </Box >
        </>
    );

    const commonModals = (
        <>
            <ModalComponent
                modalOpen={modalType === 'phone_required_create'}
                onClose={() => setModalType(null)}
                title="Aggiungi il tuo numero di telefono"
                message="Per creare un tavolo devi completare il tuo profilo con un numero di telefono."
                buttons={[
                    { text: 'Annulla', onClick: () => setModalType(null), color: 'secondary', variant: 'outlined' },
                    { text: 'Completa il profilo', onClick: goToOwnProfile }
                ]}
                disableBackdropClick
            />
            <ModalComponent
                modalOpen={modalType === 'phone_required_join'}
                onClose={() => setModalType(null)}
                title="Completa il tuo profilo"
                message="Per partecipare a un tavolo servono nome, cognome e cellulare nel tuo profilo. Completa i dati, poi torna al tavolo per iscriverti."
                buttons={[
                    { text: 'Annulla', onClick: () => setModalType(null), color: 'secondary', variant: 'outlined' },
                    { text: 'Completa il profilo', onClick: goToOwnProfile }
                ]}
                disableBackdropClick
            />
            <ModalComponent
                modalOpen={modalType === 'join_warning'}
                onClose={() => setModalType(null)}
                title="Prima di unirti"
                message="Dopo la conferma potrai scrivere ai commensali nella chat del tavolo."
                buttons={[
                    { text: 'Annulla', onClick: () => setModalType(null), color: 'secondary' },
                    { text: 'Unisciti al tavolo', onClick: () => { setModalType(null); handleJoinTable(); } }
                ]}
            />
            <ModalComponent
                modalOpen={modalType === 'created'}
                onClose={handleCloseModal}
                title="Tavolo creato con successo."
                message={
                    (() => {
                        let returnedMessage = "Il tavolo è stato creato. \n\n";
                        returnedMessage += "ATTENZIONE: Ricordati di prenotare il locale. \n\n";
                        if (latLonPlace.phone !== '') {
                            returnedMessage += "Per particolari esigenze e per prenotare puoi contattare il locale al numero " + latLonPlace.phone + ".\n\n";
                        }
                        returnedMessage += "Visualizza al tavolo con il tasto qui sotto.";
                        return returnedMessage;
                    })()
                }
                buttons={[
                    {
                        text: 'Condividi Tavolo',
                        onClick: handleShareTable,
                        color: 'secondary',
                        variant: 'outlined'
                    },
                    {
                        text: 'Vai al tavolo',
                        onClick: handleCloseModal
                    },
                    ...(
                        latLonPlace.phone !== ''
                            ? [{
                                text: 'Contatta il locale',
                                onClick: () => window.open(`tel:${latLonPlace.phone}`),
                                color: 'secondary'
                            }]
                            : []
                    )
                ]}
                disableBackdropClick
            />
            <ModalComponent
                modalOpen={modalType === 'joined'}
                onClose={() => setModalType(null)}
                title="Partecipazione confermata"
                message="Sei ora iscritto al tavolo."
                buttons={[{ text: "Vai al tavolo", onClick: () => setModalType(null) }]}
            />
            <ModalComponent
                modalOpen={modalType === 'updated'}
                onClose={handleCloseModal}
                title="Tavolo aggiornato con successo."
                message="Il tuo tavolo è stato aggiornato."
                buttons={[{ text: "Vai al tavolo", onClick: handleCloseModal }]}
                disableBackdropClick
            />
            <ModalComponent
                modalOpen={modalType === 'reported'}
                onClose={handleCloseModal}
                title="Segnalazione inviata"
                message="Hai segnalato questo tavolo. Lo rimuoveremo se viola il codice di condotta."
                buttons={[{ text: "OK", onClick: handleCloseModal }]}
            />
            <ModalComponent
                modalOpen={modalErrorOpen}
                onClose={() => setModalErrorOpen(false)}
                title="Attenzione campi mancanti"
                message={modalErrorMessage}
                buttons={[{ text: "Completa i dati", onClick: () => setModalErrorOpen(false) }]}
                disableBackdropClick
            />
            <Snackbar open={snackbarMessage !== ''} autoHideDuration={6000} onClose={() => setSnackbarMessage('')}>
                <Alert onClose={() => setSnackbarMessage('')} severity="info" sx={{ width: '100%' }}>
                    {snackbarMessage}
                </Alert>
            </Snackbar>
        </>
    );

    if (group_id && (detailLoad.groupId !== group_id || detailLoad.userId !== userId || detailLoad.status !== 'ready')) {
        const loading = detailLoad.groupId !== group_id || detailLoad.status === 'loading';
        return (
            <Box sx={{ minHeight: '100dvh', display: 'grid', placeItems: 'center', p: 3, textAlign: 'center' }} role="status">
                {loading ? (
                    <Box>
                        <CircularProgress aria-label="Caricamento del tavolo" />
                        <Typography sx={{ mt: 2 }}>Caricamento del tavolo…</Typography>
                    </Box>
                ) : (
                    <Box>
                        <Typography variant="h5" gutterBottom>
                            {detailLoad.status === 'not-found' ? 'Tavolo non trovato' : 'Impossibile caricare il tavolo'}
                        </Typography>
                        <Typography sx={{ mb: 2 }}>
                            {detailLoad.status === 'not-found'
                                ? 'Non riusciamo a trovare il tavolo. Prova a ricaricare.'
                                : 'Controlla la connessione e riprova.'}
                        </Typography>
                        {detailLoad.status === 'not-found' && (
                            <Typography variant="caption" display="block" sx={{ mb: 2 }}>
                                Codice tavolo: {group_id}
                            </Typography>
                        )}
                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', flexWrap: 'wrap' }}>
                            <Button variant="contained" onClick={() => { clearSessionCache(); setDetailRetry((retry) => retry + 1); }}>Ricarica</Button>
                            {detailLoad.status === 'not-found' && (
                                <Button variant="outlined" onClick={() => navigate('/index', { replace: true })}>
                                    Vai alla home
                                </Button>
                            )}
                        </Box>
                    </Box>
                )}
            </Box>
        );
    }

    if (!group_id && (!userId || creationProfileStatus !== 'ready')) {
        return (
            <>
                <Header title="Crea un tavolo" />
                <Box sx={{ mt: 'var(--cv-top-bar-height)', p: 3, textAlign: 'center' }}>
                    {creationProfileStatus === 'loading' || !userId ? (
                        <CircularProgress aria-label="Verifica del profilo" />
                    ) : creationProfileStatus === 'error' ? (
                        <>
                            <Typography>Non è stato possibile verificare il profilo. Riprova.</Typography>
                            <Button onClick={() => setProfileCheckRetry((value) => value + 1)}>Riprova</Button>
                        </>
                    ) : (
                        <>
                            <Typography variant="h5">Completa il profilo prima di creare un tavolo</Typography>
                            <Typography sx={{ my: 2 }}>Inserisci nome, cognome e numero di telefono nel tuo profilo.</Typography>
                            <Button variant="contained" onClick={goToOwnProfile}>Completa il profilo</Button>
                        </>
                    )}
                </Box>
            </>
        );
    }

    if (viewState === 'create' || viewState === 'edit') {
        const canEditSchedule = viewState !== 'edit' || attendeeCount <= 1;
        const canDeleteTable = viewState === 'edit' && status === 'schedulato' && attendeeCount <= 1;
        return (
            <>
                <TableCreateView
                    tableTheme={tableTheme}
                    setTableTheme={setTableTheme}
                    tableStartAt={tableStartAt}
                    setTableStartAt={setTableStartAt}
                    tableLocationName={tableLocationName}
                    handleSearchSelection={handleSearchSelection}
                    centerFromLocalStorage={centerFromLocalStorage}
                    description={description}
                    setDescription={setDescription}
                    interests={interests}
                    setInterests={setInterests}
                    visibility={visibility}
                    setVisibility={setVisibility}
                    selectedRuleIds={selectedRuleIds}
                    setSelectedRuleIds={setSelectedRuleIds}
                    tableRuleOptions={tableRuleOptions}
                    coverImageUrl={coverImageUrl}
                    setCoverImageUrl={setCoverImageUrl}
                    setCoverFile={setCoverFile}
                    setCoverSource={setCoverSource}
                    numberParticipants={numberParticipants}
                    setNumberParticipants={setNumberParticipants}
                    handleCreateTable={handleCreateTable}
                    handleUpdateTable={handleUpdateTable}
                    handleShareTable={handleShareTable}
                    handleOpenChat={handleOpenChat}
                    isLoading={isLoading}
                    errorMessage={errorMessage}
                    viewState={viewState}
                    canEditSchedule={canEditSchedule}
                    canDeleteTable={canDeleteTable}
                    handleCancelTable={handleCancelTable}
                    attendeeCount={attendeeCount}
                    participants={participants}
                    userId={userId}
                />
                {commonModals}
            </>
        );
    }

    return (
        <>
            <TableDetailView
                tableTheme={tableTheme}
                tableStartAt={tableStartAt}
                tableEndAt={tableEndAt}
                tableLocationName={tableLocationName}
                latLonPlace={latLonPlace}
                description={description}
                interests={interests}
                selectedRuleIds={selectedRuleIds}
                tableRuleOptions={tableRuleOptions}
                coverImageUrl={coverImageUrl}
                participants={participants}
                attendeeCount={attendeeCount}
                numberParticipants={numberParticipants}
                status={status}
                userId={userId}
                isUserAttending={isUserAttending}
                isCreator={isCreator}
                viewState={viewState}
                handleJoinTable={handleJoinRequest}
                handleOpenWaitlist={handleOpenWaitlist}
                handleShareTable={handleShareTable}
                handleReportTable={handleReportTable}
                handleCancelAttendee={handleCancelAttendee}
                selectedLeaveReasons={selectedLeaveReasons}
                setSelectedLeaveReasons={setSelectedLeaveReasons}
                leaveReasonOptions={leaveReasonOptions}
                handleOpenChat={handleOpenChat}
                isLoading={isLoading}
                errorMessage={errorMessage}
                requestState={requestState}
                onLoginRequired={() => {
                    const redirectTo = group_id ? `/table/${group_id}` : '/table';
                    const handledByNative = requestNativeLogin(redirectTo);

                    if (!handledByNative) {
                        navigate('/login', { state: { redirectTo } });
                    }
                }}
                groupId={group_id}
                navigateToFeedback={(id) => navigate('/feedback/' + id)}
            />
            {commonModals}
        </>
    );
}
